import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, TrendingDown, Clock, ArrowRight } from "lucide-react";
import type { DashboardSummary } from "@/lib/calculationEngine";
import type { RedirectedFundingSummary } from "@/lib/lumpSumEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";

interface SummaryCardsProps {
  summary: DashboardSummary;
  jurisdiction: Jurisdiction;
  desiredPayment?: number;
  isInterestOnly?: boolean;
}

export function SummaryCards({ summary, jurisdiction, desiredPayment, isInterestOnly }: SummaryCardsProps) {
  const { fmt } = createFormatter(jurisdiction);
  const displayPayment = desiredPayment && desiredPayment > 0 ? desiredPayment : summary.targetPaymentPerPeriod;
  const policyVolPct = (summary.policyLoanInterestVolume * 100).toFixed(2);
  const standardVolPct = (summary.standardLoanInterestVolume * 100).toFixed(2);
  const earlyPeriods = summary.earlyPayoffPeriods;

  return (
    <div className="space-y-4">
      {summary.hasLapseRiskWarning && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm font-medium text-destructive">
            Warning: Total indebtedness exceeds available cash value. This policy may be at risk of lapse.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Interest on Policy Loans</p>
            <p className="text-[10px] text-muted-foreground">(Paid to Life Company / Your Co-Ownership Pool)</p>
            <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1">{fmt(summary.policyLoanTotalInterest)}</p>
          </CardContent>
        </Card>
        {!isInterestOnly && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Interest for Repayment Strategy</p>
              <p className="text-[10px] text-muted-foreground">(If this were an Intended Loan)</p>
              <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1">{fmt(summary.bankLoanTotalInterest)}</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {isInterestOnly ? 'Interest-Only Payment Per Period' : 'Target Payment Per Period'}
            </p>
            {isInterestOnly && (
              <p className="text-[10px] text-muted-foreground">(No principal reduction — interest only)</p>
            )}
            <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1">{fmt(displayPayment)}</p>
          </CardContent>
        </Card>
        {!isInterestOnly && (
          <Card className="border-accent/30 bg-accent/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="h-4 w-4 text-accent" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Interest Volume</p>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Policy Loan</p>
                  <p className="text-lg sm:text-xl font-semibold tabular-nums text-accent">{policyVolPct}%</p>
                  <p className="text-[10px] text-muted-foreground">
                    {fmt(summary.policyLoanTotalInterest)} interest of {fmt(summary.totalPaymentsApplied)} total paid
                  </p>
                </div>
                <div className="border-t border-border pt-2">
                  <p className="text-xs text-muted-foreground">Intended Loan</p>
                  <p className="text-lg sm:text-xl font-semibold tabular-nums">{standardVolPct}%</p>
                  <p className="text-[10px] text-muted-foreground">
                    {fmt(summary.bankLoanTotalInterest)} interest of {fmt(summary.standardLoanTotalPayments)} total paid
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Lower interest volume = more of your payment goes to principal</p>
            </CardContent>
          </Card>
        )}
        {!isInterestOnly && (
          <Card className="border-accent/30 bg-accent/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-positive" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Early Payoff</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <p className="text-xs text-muted-foreground">Committed Loan Schedule</p>
                  <p className="text-base sm:text-lg font-semibold tabular-nums">{summary.standardLoanPaymentCount} payments</p>
                </div>
                <div className="flex justify-between items-baseline">
                  <p className="text-xs text-muted-foreground">Policy Loan Repaid</p>
                  <p className="text-base sm:text-lg font-semibold tabular-nums text-positive">{summary.policyLoanPaymentCount} payments</p>
                </div>
                {earlyPeriods > 0 && (
                  <div className="flex justify-between items-baseline border-t border-border pt-2">
                    <p className="text-xs text-muted-foreground">Remaining Payments (Redirect into System)</p>
                    <p className="text-base sm:text-lg font-semibold tabular-nums text-accent">{earlyPeriods} payments</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        {!isInterestOnly && (
          <Card className="border-accent/30 bg-accent/5">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Additional Financing Margin</p>
              <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 text-positive">{fmt(summary.additionalIntendedFinancingMargin)}</p>
              <p className="text-xs text-muted-foreground mt-2">Suggested redirect to family system premiums</p>
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
}

/* Extracted Lump Sum Impact cards — rendered separately inside the lump sum area */
interface LumpSumImpactCardsProps {
  summary: DashboardSummary;
  lumpSumDashboard: DashboardSummary;
  redirectedFunding?: RedirectedFundingSummary;
  continueAfterPayoff?: boolean | null;
  jurisdiction: Jurisdiction;
}

export function LumpSumImpactCards({ summary, lumpSumDashboard, redirectedFunding, continueAfterPayoff, jurisdiction }: LumpSumImpactCardsProps) {
  const { fmt } = createFormatter(jurisdiction);
  return (
    <div className="space-y-4 mt-4">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <ArrowRight className="h-4 w-4 text-accent" />
        Lump Sum Impact — Updated Estimates
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-positive/30 bg-positive/5">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Policy Loan Repaid (After Lump Sum)</p>
            <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 text-positive">
              {lumpSumDashboard.policyLoanPaymentCount} payments
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <p className="text-[10px] text-muted-foreground">
                Original: {summary.policyLoanPaymentCount} payments
              </p>
              {lumpSumDashboard.policyLoanPaymentCount < summary.policyLoanPaymentCount && (
                <span className="text-[10px] font-semibold text-positive">
                  ({summary.policyLoanPaymentCount - lumpSumDashboard.policyLoanPaymentCount} fewer)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-positive/30 bg-positive/5">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated Financing Margin</p>
            <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 text-positive">
              {fmt(lumpSumDashboard.additionalIntendedFinancingMargin)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-2">
              Original: {fmt(summary.additionalIntendedFinancingMargin)}
            </p>
          </CardContent>
        </Card>

        {redirectedFunding && redirectedFunding.remainingPaymentsAfterPayoff > 0 && continueAfterPayoff === true && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Payments Redirect to System Funding</p>
              <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 text-primary">
                {fmt(redirectedFunding.totalRedirectedAmount)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-2">
                {redirectedFunding.remainingPaymentsAfterPayoff} payments × {fmt(redirectedFunding.paymentAmount)} redirected
              </p>
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                Policy Loan Repaid — Payment behavior continues as family financing strategy funding.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
